import type {
  BuyerInterviewAnswerDto,
  BuyerInterviewConditionDto,
  BuyerInterviewQuestionDto,
  BuyerInterviewRegistryDto,
  BuyerQualificationDimension,
} from "@/api/buyers";

/**
 * Shared vocabulary for the qualification interview surfaces.
 *
 * <p>🔴 <b>Nothing here scores anything.</b> The registry is served, the
 * verdicts are computed server-side and revealed only on completion; this file
 * holds display labels and the two pure predicates the drawer needs — which
 * branch is open, and whether an answer was recorded. There is deliberately no
 * "strength", "weight" or "expected answer" anywhere in it, because a hint of
 * one in the drawer would steer what the specialist writes down.</p>
 */

/**
 * The six dimensions, in the order the interview walks them.
 *
 * <p>🔴 <b>Financing is GONE.</b> It was the fifth dropdown of the retired
 * editor; that picture lives in the prequalification system now. A historical
 * `BuyerLeadQualification` row still carries a financing verdict and still
 * renders it — but nothing new writes one, and no interview produces one.</p>
 */
export const DIMENSION_ORDER: readonly BuyerQualificationDimension[] = [
  "FinancialCapacity",
  "Geography",
  "Experience",
  "Timeline",
  "DecisionAuthority",
  "MotivationFit",
] as const;

/** Exhaustive `Record`, so adding a dimension to the union fails typecheck here
 *  rather than rendering a raw enum name on the tile. */
export const DIMENSION_LABELS: Record<BuyerQualificationDimension, string> = {
  FinancialCapacity: "Financial capacity",
  Geography: "Geography",
  Experience: "Experience",
  Timeline: "Timeline",
  DecisionAuthority: "Decision authority",
  MotivationFit: "Motivation & fit",
};

/**
 * Each registry section is scored as exactly one dimension, and the server
 * names the section ids (`BuyerInterviewRegistry.Section*`). Exhaustive over
 * the dimension union, so a seventh dimension fails typecheck here.
 */
export const DIMENSION_SECTION: Record<BuyerQualificationDimension, string> = {
  FinancialCapacity: "FC",
  Geography: "GEO",
  Experience: "EXP",
  Timeline: "TL",
  DecisionAuthority: "DA",
  MotivationFit: "MF",
};

/** The dimension a section is scored as, or null for an unknown section. */
export function dimensionForSection(
  sectionId: string,
): BuyerQualificationDimension | null {
  const hit = DIMENSION_ORDER.find((d) => DIMENSION_SECTION[d] === sectionId);
  return hit ?? null;
}

/** Appended to a stored option key the registry no longer offers. The answer
 *  was recorded; it must never render blank just because the option list moved
 *  on. */
export const RETIRED_CHOICE_NOTE = "(choice no longer in use)";

export function dimensionLabel(dimension: string): string {
  return (
    DIMENSION_LABELS[dimension as BuyerQualificationDimension] ?? dimension
  );
}

/**
 * A section's heading in one wording version: its TITLE from that version's
 * registry (ruling Q3 — the record, print and compare views head each section
 * in the wording it was asked with). Falls back to the coded dimension name for
 * a section the registry does not carry. The outcome, the Assessment card and
 * "Counts toward Fail in X" keep the coded names ({@link dimensionLabel}).
 */
export function sectionTitle(
  registry: BuyerInterviewRegistryDto | undefined,
  sectionId: string,
): string {
  const title = registry?.sections.find((s) => s.id === sectionId)?.title?.trim();
  if (title) return title;
  const dimension = dimensionForSection(sectionId);
  return dimension ? dimensionLabel(dimension) : sectionId;
}

/** The local shape of one in-progress answer. `text` is a plain string rather
 *  than `string | null` because it is bound straight to an input. */
export interface DraftAnswer {
  choices: string[];
  text: string;
}

export type DraftAnswers = Record<string, DraftAnswer>;

export const BLANK_ANSWER: DraftAnswer = { choices: [], text: "" };

export function toDraftAnswers(
  answers: Record<string, BuyerInterviewAnswerDto> | undefined,
): DraftAnswers {
  const out: DraftAnswers = {};
  for (const [id, a] of Object.entries(answers ?? {})) {
    out[id] = { choices: [...(a.choices ?? [])], text: a.text ?? "" };
  }
  return out;
}

/**
 * Was anything recorded?
 *
 * <p>Mirrors the server's own `IsAnswered`: any choice, or any non-blank text.
 * A blank IS "nothing recorded" — there is no separate flag, because an
 * interview answer is exactly what was written down.</p>
 */
export function isRecorded(answer: DraftAnswer | undefined): boolean {
  if (!answer) return false;
  return answer.choices.length > 0 || answer.text.trim().length > 0;
}

/** A branch's optional SECOND parent (owner ruling Q9, option B), served as
 *  `branch.alsoRequires`. */
export type BuyerInterviewCondition = BuyerInterviewConditionDto;

function carries(answers: DraftAnswers, condition: BuyerInterviewCondition): boolean {
  const parent = answers[condition.parentQuestionId];
  if (!parent) return false;
  return condition.whenAnyOf.some((key) => parent.choices.includes(key));
}

/**
 * Is this question visible?
 *
 * <p>A question with no branch is always visible. A branch opens when the
 * PARENT'S recorded choices include any of `whenAnyOf` — the same condition the
 * server evaluates, read off the same frozen option keys. A branch with a
 * second parent (`alsoRequires`, e.g. MF3b: not managing it full time AND a company
 * is buying) opens only when BOTH carry one of their keys.</p>
 */
export function isVisible(
  question: BuyerInterviewQuestionDto,
  answers: DraftAnswers,
): boolean {
  const branch = question.branch;
  if (!branch) return true;
  if (!carries(answers, branch)) return false;
  return branch.alsoRequires ? carries(answers, branch.alsoRequires) : true;
}

/**
 * Was this question asked, for a READ-ONLY view (the transcript, the record,
 * its print view, the comparison)?
 *
 * <p>Its branch was open, and — for a question retired from the live interview
 * (the one-call brief, D2) — something was recorded against it. A retired
 * question is never asked in a new interview, so a blank one is not "Not
 * recorded", it is not part of the interview at all; but a past interview that
 * DID record it still shows that answer (A7). The one predicate every read-only
 * surface uses, so they cannot disagree about which rows exist.</p>
 */
export function isAsked(question: BuyerInterviewQuestionDto, answers: DraftAnswers): boolean {
  return isVisible(question, answers) && (!question.retired || isRecorded(answers[question.id]));
}

/**
 * Is this question put to the buyer in the LIVE call?
 *
 * <p>Never a retired question; never one wholly derived from the call's
 * prequalification answers (`callSource: "derived"`); and a
 * `derivedWhenAnswered` question (TL1) only while the server has NOT derived it
 * — `derivedQuestionIds` lists the ones it has. A `partlyDerived` question is
 * asked, but only for the choices in `callAskedOptionKeys`
 * ({@link liveOptions}).</p>
 */
export function isAskedLive(
  question: BuyerInterviewQuestionDto,
  answers: DraftAnswers,
  derivedQuestionIds: ReadonlySet<string>,
): boolean {
  if (question.retired) return false;
  if (question.callSource === "derived") return false;
  if (question.callSource === "derivedWhenAnswered" && derivedQuestionIds.has(question.id)) {
    return false;
  }
  return isVisible(question, answers);
}

/** The choices the live call offers: for a `partlyDerived` question only the
 *  keys in `callAskedOptionKeys` (none at all for EXP3, whose text box is the
 *  whole question on the call); every option otherwise. */
export function liveOptions(question: BuyerInterviewQuestionDto) {
  if (question.callSource !== "partlyDerived") return question.options;
  const asked = question.callAskedOptionKeys ?? [];
  return question.options.filter((o) => asked.includes(o.key));
}

/**
 * The answers the live call stands on: what was recorded here, with every
 * DERIVED answer taken from the server.
 *
 * <p>The server returns an in-progress interview's effective answers (derived
 * from the call's prequalification answers where the derivation produces one)
 * and names the derived ids. Local answers are the specialist's input and are
 * seeded once, so they go stale for anything the call derives; this overlays
 * the server's answer for a `derived` question, a `derivedWhenAnswered` one it
 * listed, and the derived CHOICES of a `partlyDerived` one (the asked choices
 * and the text stay local). Branch visibility and the review transcript read
 * this, so a follow-up a derived answer opens (EXP1 → EXP1a) is asked.</p>
 */
export function effectiveLiveAnswers(
  registry: BuyerInterviewRegistryDto | undefined,
  local: DraftAnswers,
  server: DraftAnswers,
  derivedQuestionIds: ReadonlySet<string>,
): DraftAnswers {
  const out: DraftAnswers = { ...local };
  for (const question of registry?.questions ?? []) {
    const id = question.id;
    const fromServer = server[id] ?? BLANK_ANSWER;
    if (
      question.callSource === "derived"
      || (question.callSource === "derivedWhenAnswered" && derivedQuestionIds.has(id))
    ) {
      out[id] = fromServer;
    } else if (question.callSource === "partlyDerived") {
      const asked = question.callAskedOptionKeys ?? [];
      const mine = local[id] ?? BLANK_ANSWER;
      out[id] = {
        choices: [
          ...fromServer.choices.filter((k) => !asked.includes(k)),
          ...mine.choices.filter((k) => asked.includes(k)),
        ],
        text: mine.text,
      };
    }
  }
  return out;
}

/** The questions of one section, in registry order. */
export function questionsForSection(
  registry: BuyerInterviewRegistryDto | undefined,
  sectionId: string,
): BuyerInterviewQuestionDto[] {
  return (registry?.questions ?? []).filter((q) => q.sectionId === sectionId);
}

/**
 * One answer, rendered as a sentence for the review transcript.
 *
 * <p>Option LABELS rather than keys: a key is a storage token nobody reading a
 * transcript should have to decode. An unknown key (an answer recorded before a
 * question was re-worded) falls back to the key itself, marked
 * {@link RETIRED_CHOICE_NOTE}, rather than vanishing — a recorded answer must
 * never render as blank.</p>
 */
export function describeAnswer(
  question: BuyerInterviewQuestionDto,
  answer: DraftAnswer | undefined,
): string {
  if (!isRecorded(answer)) return "";
  const chosen = (answer?.choices ?? []).map((key) => {
    const option = question.options.find((o) => o.key === key);
    return option ? option.label : `${key} ${RETIRED_CHOICE_NOTE}`;
  });
  const text = answer?.text.trim() ?? "";
  if (chosen.length === 0) return text;
  if (!text) return chosen.join(", ");
  return `${chosen.join(", ")} — ${text}`;
}
